/**
 * Live profile change notifications — a profile created, re-pinned or rolled
 * back, and a member listed on or taken off one — used purely as invalidation
 * triggers. Every frame names its profile, and each subscriber receives that
 * name, because most consumers care about only some profiles: the collectors
 * fleet reads `core` alone, since a role profile's bundle never reaches a host.
 *
 * A member change is its own stream: it moves only what the profile's next
 * repin would pin, never what a host or a dispatch is served, so the consumers
 * of the pin changes never hear it. Its frame also names the artefact, whose
 * own list of the profiles holding it changes with it.
 */

import type {
  ProfileBroadcastPayload,
  ProfileMembershipBroadcastPayload,
} from '@/types/bootstrapProfiles'
import { WebSocketClient } from '../client'
import { SubscriptionRegistry } from '../subscriptions'

/**
 * Every profile frame the backend announces. The backend's contract tier holds
 * this tuple to its audit registry, and each frame is heard by exactly one of
 * the two streams below.
 */
export const PROFILE_EVENTS = [
  'pmem_profile_created',
  'pmem_profile_repinned',
  'pmem_profile_rolled_back',
  'pmem_profile_member_added',
  'pmem_profile_member_removed',
] as const

type ProfileEvent = (typeof PROFILE_EVENTS)[number]

/** The frames of a profile created, re-pinned or rolled back, carrying `ProfileBroadcastPayload`. */
export const PROFILE_CHANGE_EVENTS = [
  'pmem_profile_created',
  'pmem_profile_repinned',
  'pmem_profile_rolled_back',
] as const satisfies readonly ProfileEvent[]

/** The frames of a member listed or taken off, carrying `ProfileMembershipBroadcastPayload`. */
export const PROFILE_MEMBERSHIP_EVENTS = [
  'pmem_profile_member_added',
  'pmem_profile_member_removed',
] as const satisfies readonly ProfileEvent[]

export class ProfilesLiveApi {
  private readonly changes: SubscriptionRegistry<null, string>
  private readonly memberships: SubscriptionRegistry<
    null,
    ProfileMembershipBroadcastPayload
  >

  constructor(private readonly client: WebSocketClient) {
    // Both streams join the shared `global_events` room; subscribe_global is
    // idempotent server-side. There is deliberately no leave — the
    // activity-feed subscription shares the room, and leaving would break that
    // subscriber.
    const join = () => this.client.emitWhenConnected('subscribe_global', {})
    this.changes = new SubscriptionRegistry({ join })
    this.memberships = new SubscriptionRegistry({ join })
  }

  /**
   * Subscribe to profile changes so a consumer can refresh server-derived
   * state. Fires on a create, a repin and a rollback of any profile — one
   * shared handler covers all three — with the name of the profile changed.
   */
  subscribeToProfileChange(cb: (name: string) => void): () => void {
    if (!this.client.hasSocket()) {
      console.warn('WebSocket not connected. Call connect() first.')
      return () => {}
    }

    for (const event of PROFILE_CHANGE_EVENTS) {
      this.client.attachOnce(event, (frame) =>
        this.changes.dispatch(null, (frame as ProfileBroadcastPayload).name)
      )
    }

    return this.changes.subscribe(null, cb)
  }

  /**
   * Subscribe to member list changes: an artefact listed on or taken off any
   * profile. The callback gets the whole frame, which names the profile and
   * the artefact.
   */
  subscribeToProfileMembershipChange(
    cb: (frame: ProfileMembershipBroadcastPayload) => void
  ): () => void {
    if (!this.client.hasSocket()) {
      console.warn('WebSocket not connected. Call connect() first.')
      return () => {}
    }

    for (const event of PROFILE_MEMBERSHIP_EVENTS) {
      this.client.attachOnce(event, (frame) =>
        this.memberships.dispatch(
          null,
          frame as ProfileMembershipBroadcastPayload
        )
      )
    }

    return this.memberships.subscribe(null, cb)
  }

  /** Re-join global_events for both streams' listeners after a reconnect. */
  replay(): void {
    this.changes.replay()
    this.memberships.replay()
  }

  clear(): void {
    this.changes.clear()
    this.memberships.clear()
  }
}
